/**
 * AgentBridgeAdmin — /admin/agent-bridge
 *
 * Timeline conversation entre l'agent du HUB et l'agent de ce pod.
 * Auto-refresh toutes les 8s.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Send, RefreshCcw, MessageSquare, Bot, User, AlertTriangle, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  note:    { label: "Note",     color: "bg-slate-100 text-slate-700" },
  request: { label: "Demande",  color: "bg-amber-100 text-amber-800" },
  answer:  { label: "Réponse",  color: "bg-emerald-100 text-emerald-700" },
  alert:   { label: "Alerte",   color: "bg-red-100 text-red-700" },
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString("fr-FR"); }
  catch { return iso; }
}

export default function AgentBridgeAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ subject: "", body: "", kind: "note" });
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/agent-bridge/messages");
      setItems(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [items.length]);

  const send = async () => {
    if (!draft.body.trim()) return toast.error("Message vide");
    setSending(true);
    try {
      const r = await api.post("/admin/agent-bridge/messages", draft);
      toast.success(r.data?.mirrored ? "Envoyé au HUB" : "Persisté localement (HUB non joignable)");
      setDraft({ subject: "", body: "", kind: "note" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-agent-bridge">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-1">Hub Agence MSC</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2 text-slate-900">
            <MessageSquare className="w-6 h-6"/> Agent Bridge
          </h1>
          <p className="text-sm text-slate-500 mt-1">Canal de communication entre l'agent HUB et l'agent de ce pod.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCcw className="w-4 h-4 mr-1"/>Rafraîchir</Button>
      </header>

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col h-[60vh]">
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Chargement…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30"/>
              Aucun message pour le moment.
            </div>
          ) : items.map(m => {
            const isOutbound = m.direction === "outbound" || m.sender === "pod-agent";
            const meta = KIND_META[m.kind] || KIND_META.note;
            const Icon = m.kind === "alert" ? AlertTriangle : m.sender === "human" ? User : Bot;
            const Arrow = isOutbound ? ArrowUpRight : ArrowDownLeft;
            return (
              <div
                key={m.id}
                className={`max-w-[75%] rounded-2xl p-4 ${isOutbound ? "ml-auto bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"} border`}
                data-testid={`agent-msg-${m.id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="w-3.5 h-3.5 text-slate-500"/>
                  <span className="text-xs font-bold text-slate-700">{m.sender}</span>
                  <Arrow className="w-3 h-3 text-slate-400"/>
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                  <span className="ml-auto text-[10px] text-slate-400">{fmt(m.created_at)}</span>
                </div>
                {m.subject && <div className="font-display font-bold text-slate-900 mb-1 text-sm">{m.subject}</div>}
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</div>
                {m.refs && Object.keys(m.refs).length > 0 && (
                  <div className="mt-2 text-[10px] text-slate-400 font-mono">refs: {JSON.stringify(m.refs)}</div>
                )}
              </div>
            );
          })}
          <div ref={endRef}/>
        </div>

        <div className="border-t p-4 bg-slate-50 space-y-2">
          <div className="flex gap-2">
            <Input
              value={draft.subject}
              onChange={e => setDraft({...draft, subject: e.target.value})}
              placeholder="Sujet (optionnel)"
              className="flex-1"
              data-testid="agent-bridge-subject"
            />
            <select
              value={draft.kind}
              onChange={e => setDraft({...draft, kind: e.target.value})}
              className="h-10 px-3 rounded-md border bg-white text-sm"
            >
              {Object.entries(KIND_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Textarea
              rows={2}
              value={draft.body}
              onChange={e => setDraft({...draft, body: e.target.value})}
              placeholder="Écrire à l'agent du HUB…"
              className="flex-1 resize-none"
              data-testid="agent-bridge-body"
            />
            <Button
              onClick={send}
              disabled={sending || !draft.body.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 self-stretch px-5"
              data-testid="agent-bridge-send"
            >
              <Send className="w-4 h-4"/>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
